import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { NodeViewWrapper, isNumber } from '@tiptap/react';
import { useRef, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { useQueryResourceDetail } from '../../../../resources/queries/useQueryResourceDetail.js';
import { isResourceDetailAudio, isResourceDetailImage } from '../../../../resources/utils/resourceDetailGuards.js';
import { isResourceStateProcessing } from '../../../../resources/utils/isResourceStateProcessing.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { ButtonArrowOpen } from './ButtonArrowOpen.js';
import { EmbedSimpleView } from './PreviewSimple.js';

const EmbedResourceComponent = ({ node, editor, updateAttributes, deleteNode }) => {
  const { src: srcProp, alt, mimeType, title, extension, width, height, resourceId, uploadProgress, kind, parentResourceId, "data-uuid": nodeUuid } = node.attrs;
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const nodeRef = useRef(node);
  nodeRef.current = node;
  const updateAttributesRef = useRef(updateAttributes);
  updateAttributesRef.current = updateAttributes;
  const { data, isError } = useQueryResourceDetail({
    resourceId,
    includeHidden: true
  }, {
    retry: 2
  });
  const src = srcProp || data?.thumbnail?.xl || data?.fileUrl;
  useEffect(() => {
    const node2 = nodeRef.current;
    if (!data) {
      return;
    }
    const src2 = node2.attrs.src;
    const resourceSrc = data?.thumbnail?.original || data?.fileUrl || src2;
    updateAttributesRef.current({
      src: resourceSrc,
      title: data.name,
      alt: data.name,
      extension: data.extension,
      uploadProgress: null,
      kind: data.kind,
      mimeType: data.mimeType
    });
  }, [
    data
  ]);
  const ref = useRef(null);
  const [spinnerPosition, setSpinnerPosition] = useState({
    x: 0,
    y: 0
  });
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0
  });
  useEffect(() => {
    if (!ref.current) return;
    const updatePosition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      setSpinnerPosition({
        x: rect.width / 2 - 16,
        y: rect.height / 2 - 16
      });
      setImageSize({
        width: rect.width,
        height: rect.height
      });
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [
    ref
  ]);
  const kindOrSpace = data ? data?.id === data?.root?.id ? "space" : data.kind : kind;
  const isTranscribing = Boolean(data && isResourceDetailAudio(data) && data.data.voiceNote === true && isResourceStateProcessing(data.stateProcessing));
  const imageWidth = width || (data && isResourceDetailImage(data) ? data.data.attributes?.dimensions?.width : null);
  return /* @__PURE__ */ jsx(StyledNodeViewWrapper, {
    children: /* @__PURE__ */ jsx(InnerContainer, {
      draggable: true,
      "data-drag-handle": true,
      children: (mimeType?.startsWith("image/") || kind === "image") && src ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          !data && isNumber(uploadProgress) && ref && spinnerPosition.x > 0 && spinnerPosition.y > 0 && /* @__PURE__ */ jsx(Spinner, {
            size: 32,
            thickness: 4,
            style: {
              position: "absolute",
              top: spinnerPosition.y,
              left: spinnerPosition.x
            }
          }),
          /* @__PURE__ */ jsx(Image, {
            ref,
            src,
            alt: alt ?? void 0,
            title: title ?? void 0,
            width: width ?? "auto",
            height: height ?? void 0,
            "data-resizeable": true,
            isLoading: !!uploadProgress
          }),
          data?.id && kindOrSpace && /* @__PURE__ */ jsx(ButtonArrowOpen, {
            resourceId: data.id,
            kind: kindOrSpace,
            style: {
              position: "absolute",
              zIndex: 10,
              ...imageWidth && Number(imageWidth) < 100 ? {
                top: 0,
                left: "calc(100% + 6px)"
              } : {
                top: 4,
                right: 4
              }
            }
          }),
          /* @__PURE__ */ jsx("span", {
            style: {
              width: `${imageSize.width}px`,
              height: `${imageSize.height}px`,
              position: "absolute",
              top: 0,
              left: 0
            }
          })
        ]
      }) : /* @__PURE__ */ jsx(EmbedSimpleView, {
        title: data?.name || title || alt || "untitled",
        extension: data?.extension || extension,
        kind: kindOrSpace,
        resourceId: data?.id,
        mimeType: data?.mimeType,
        fileSize: data?.size,
        fileUrl: data?.fileUrl,
        notFound: isError,
        hideDropdownMenu: !editor.isEditable,
        isTranscibing: isTranscribing,
        /**
        * we don't need to pass the progress when the resource is already loaded
        */
        uploadProgress: data ? null : uploadProgress,
        deleteNode,
        /**
        * if it doesn't have a parent id, it's a root
        */
        parentResourceId: parentResourceId || data?.parent?.id || data?.id
      })
    })
  }, nodeUuid);
};
const StyledNodeViewWrapper = styled(NodeViewWrapper).withConfig({
  displayName: "EmbedResourceComponent__StyledNodeViewWrapper",
  componentId: "sc-7d6560eb-0"
})([
  `display:block;position:relative;box-shadow:none;width:max-content;max-width:100%;span:has(> &){display:inline-block;transform:translateY(2.5px);max-width:100%;}`
]);
const Image = styled.img.withConfig({
  displayName: "EmbedResourceComponent__Image",
  componentId: "sc-7d6560eb-1"
})([
  ``,
  `;object-fit:contain;border-radius:4px;`
], (p) => p.isLoading && `opacity: 0.3`);
const InnerContainer = styled.div.withConfig({
  displayName: "EmbedResourceComponent__InnerContainer",
  componentId: "sc-7d6560eb-2"
})([
  `position:relative;display:inline-block;width:max-content;max-width:100%;white-space:normal;outline-width:0px;outline-offset:0px;outline-color:transparent;.ProseMirror-selectednode & img{outline:2px solid rgba(var(--color-primary),0.3);}`
]);

export { EmbedResourceComponent };
//# sourceMappingURL=EmbedResourceComponent.js.map
